// Building views (IMPLEMENTATION §2.6 layers, §2.9 "Views"): per building a pooled body sprite (chutes on
// the chute layer), a facing arrow for machines and drills, a progress bar cropped from a baked bar (no
// Graphics; a machine facing S draws it above its arrow), the Gallery's mounted gun (the unbadged frame,
// turned juice.json board.gunMountDeg) and its three rail targets (the kind of the last fired ammo tier), a
// chute chevron on one global clock, the stall icon (error badges pulse), the moving parts (M10: the drill
// bit spins, the press piston stamps, the grinder wheel turns, the smelter mouth glows) and the contents
// indicators (render/juice/contents.ts). Every animated value comes from the building's anim records (the
// board FX write them; render/juice/boardFx.ts) and the FX clock (render/juice/idle.ts); nothing idles in
// ?test. Reconciled with the state every frame.
import { badgeShare, frameId } from '../../../art/catalog.ts';
import type { JuiceData } from '../../../data/schema.ts';
import type { SimDefs } from '../../../sim/defs.ts';
import { progressPermille, shownStall } from '../../../sim/query.ts';
import type { Building, SimState } from '../../../sim/state.ts';
import type { FxConfig } from '../../fxConfig.ts';
import {
  type BldAnims,
  type BldFx,
  type GoneFx,
  resetBldAnims,
  restBldAnims,
} from '../../juice/boardFx.ts';
import { contentsOverlays, galleryDry, type Overlay } from '../../juice/contents.ts';
import {
  arrowPose,
  barPose,
  type GeoCfg,
  gunPose,
  partPose,
  stallPose,
  TARGET_SLOTS,
  TURN_DEG,
  targetPose,
} from '../../juice/geometry.ts';
import {
  bitFrame,
  chevronPhase,
  drilling,
  glowAlpha,
  type IdleMode,
  running,
  stallPulse,
  wheelAngle,
} from '../../juice/idle.ts';
import { DEPTH } from '../../layers.ts';
import { cornerChevron, isBent } from '../../sync.ts';
import type { FrameIndex } from '../frames.ts';
import type { Spr, SprPool } from '../Spr.ts';

/** Building types whose `dir` is an output side (the facing arrow). */
const FACING: ReadonlySet<string> = new Set([
  'drill',
  'caster',
  'grinder',
  'smelter',
  'press',
  'loader',
  'packer',
  'gunsmith',
]);

export interface BuildingViewsCfg {
  fx: FxConfig;
  geo: GeoCfg;
  /** juice.json events.machine (idle animation numbers). */
  machine: JuiceData['events']['machine'];
  /** juice.json targets.byAmmoTier. */
  byAmmoTier: readonly string[];
  /** juice.json events.galleryDry.afterMs: the "No ammo" badge after the queue stayed empty this long. */
  galleryDryMs: number;
}

interface BView {
  gen: number;
  body: Spr;
  bodyFrame: string;
  /** The drill head overlay (M8: points at the deposit, rotated with the drill's `dir`). */
  head: Spr | null;
  bit: Spr | null;
  piston: Spr | null;
  wheel: Spr | null;
  glow: Spr | null;
  arrow: Spr | null;
  barBg: Spr | null;
  barFill: Spr | null;
  gun: Spr | null;
  targets: Spr[];
  chev: Spr | null;
  stall: Spr | null;
  stallId: string | null;
  /** The Gallery's "No ammo" badge (UX §5 GalleryDry), in the stall corner when no stall shows. */
  dry: Spr | null;
  /** Contents indicators (variable count). */
  extras: Spr[];
  wx: number;
  wy: number;
}

/** A building of `s` by id (the list is in ascending id order, M2 #1). */
function byId(s: SimState, id: number): Building | null {
  const list = s.buildings;
  let lo = 0;
  let hi = list.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const b = list[mid] as Building;
    if (b.id === id) return b;
    if (b.id < id) lo = mid + 1;
    else hi = mid - 1;
  }
  return null;
}

export class BuildingViews {
  private readonly views = new Map<number, BView>();
  private readonly anim = new Map<number, BldAnims>();
  private readonly overlays: Overlay[] = [];
  private gen = 0;
  private stalls = 0;
  private drys = 0;
  private readonly pool: SprPool;
  private readonly frames: FrameIndex;
  private readonly wu: number;
  private readonly fx: FxConfig;
  private readonly c: BuildingViewsCfg;
  /** M6 input overlay: the building dragged in Build (hidden while its DOM ghost flies). */
  private hidden = -1;
  /** CSS px per cell (layout.cellCss): the stall badge's §2.6 minimum is in CSS px. */
  private cellCss = 0;

  constructor(pool: SprPool, frames: FrameIndex, cellWu: number, cfg: BuildingViewsCfg) {
    this.pool = pool;
    this.frames = frames;
    this.wu = cellWu;
    this.fx = cfg.fx;
    this.c = cfg;
  }

  private bodyFrame(b: Building): string {
    if (b.type === 'chute') return frameId.chute(b.inDir, b.dir);
    return frameId.building(b.type, b.type === 'deposit' ? b.ore : null, b.level);
  }

  /** Keeps an optional part in sync: acquire / reframe / release. */
  private part(cur: Spr | null, want: string | null, depth: number): Spr | null {
    if (want === null) {
      if (cur !== null) this.pool.release(cur);
      return null;
    }
    if (cur === null) return this.pool.acquire(want, depth);
    this.pool.frame(cur, want);
    return cur;
  }

  /** The anim records of building `bid` (created on demand, so an effect may start before its view). */
  anims(bid: number): BldAnims {
    let a = this.anim.get(bid);
    if (a === undefined) {
      a = restBldAnims();
      this.anim.set(bid, a);
    }
    return a;
  }

  private shareOfStall(): number {
    return this.cellCss > 0
      ? badgeShare(this.fx.stallBadge, this.fx.stallBadgeMinPx, this.cellCss)
      : this.fx.stallBadge;
  }

  /** The FX handle of building `bid` in state `s` (its cell, type and anim records), or null. */
  fxOf(s: SimState, bid: number): BldFx | null {
    const b = byId(s, bid);
    if (b === null) return null;
    const wu = this.wu;
    return {
      a: this.anims(bid),
      type: b.type,
      level: b.level,
      dir: b.dir,
      ore: b.type === 'deposit' ? b.ore : null,
      x: (b.x + 0.5) * wu,
      y: (b.y + 0.5) * wu,
      gunTier: b.type === 'gallery' && b.gun !== null ? b.gun.tier : null,
      stallShare: this.shareOfStall(),
    };
  }

  /**
   * Where building `bid` is drawn (its view, before this frame's sync drops the view of a removed building:
   * the board FX handle the frame's events first), or null.
   */
  gone(bid: number): GoneFx | null {
    const v = this.views.get(bid);
    return v === undefined
      ? null
      : { x: v.wx, y: v.wy, frame: v.bodyFrame, hidden: bid === this.hidden };
  }

  /**
   * Reconciles the views with `s` and draws them with the anim records (after the FX handled this frame's
   * events and stepped); idle animations run on `t` = the FX clock's idle time (render/juice/clock.ts).
   */
  sync(s: SimState, d: SimDefs, t: number, mode: IdleMode): void {
    this.gen += 1;
    const wu = this.wu;
    const fx = this.fx;
    const geo = this.c.geo;
    const m = this.c.machine;
    const phase = chevronPhase(t, fx.chevronCycleMs, fx.chevronPhases, mode.still);
    const pulse = stallPulse(t, fx.stallPulseHz, fx.stallPulseMin, mode.still);
    const share = this.shareOfStall();
    let stalls = 0;
    let drys = 0;
    for (const b of s.buildings) {
      const chute = b.type === 'chute';
      let v = this.views.get(b.id);
      const body = this.bodyFrame(b);
      if (v === undefined) {
        v = {
          gen: this.gen,
          body: this.pool.acquire(body, chute ? DEPTH.chute : DEPTH.building),
          bodyFrame: body,
          head: null,
          bit: null,
          piston: null,
          wheel: null,
          glow: null,
          arrow: null,
          barBg: null,
          barFill: null,
          gun: null,
          targets: [],
          chev: null,
          stall: null,
          stallId: null,
          dry: null,
          extras: [],
          wx: 0,
          wy: 0,
        };
        this.views.set(b.id, v);
      } else if (v.bodyFrame !== body) {
        this.pool.frame(v.body, body);
        v.bodyFrame = body;
        v.body.setDepth(chute ? DEPTH.chute : DEPTH.building);
      }
      v.gen = this.gen;
      const an = this.anims(b.id);
      const k = an.body;
      const cx = (b.x + 0.5) * wu;
      const cy = (b.y + 0.5) * wu;
      v.wx = cx;
      v.wy = cy;
      const hidden = b.id === this.hidden;
      const baseA = b.ghost !== null ? fx.ghostAlpha : b.paused ? fx.pausedAlpha : 1;
      const tint = b.paused ? fx.pausedTint : -1;
      const bx = cx + k.dx;
      const by = cy + k.dy;
      v.body.place(bx, by, wu * k.sx, wu * k.sy);
      v.body.angle(k.rot);
      v.body.alpha(hidden ? 0 : baseA * clamp01(k.a));
      v.body.setTint(tint);

      // the drill head (drawn pointing N) turned toward the deposit the drill works (its `dir`) + its bit
      const drill = b.type === 'drill';
      v.head = this.part(v.head, drill ? frameId.drillHead(b.level) : null, DEPTH.building + 0.5);
      if (v.head !== null) {
        v.head.place(bx, by, wu * k.sx, wu * k.sy);
        v.head.angle(TURN_DEG[b.dir] + k.rot);
        v.head.alpha(hidden ? 0 : baseA * clamp01(k.a));
        v.head.setTint(tint);
      }
      const bitPose = drill ? partPose('drill', 'bit', bx, by, wu, b.dir) : null;
      const bi = bitFrame(t, m.drillSpinMs, drill && drilling(d, b), mode);
      v.bit = this.part(v.bit, bitPose ? frameId.part('drill', 'bit', bi) : null, DEPTH.part);
      if (v.bit !== null && bitPose !== null) {
        v.bit.place(bitPose.x, bitPose.y, bitPose.w, bitPose.h);
        v.bit.angle(bitPose.rot);
        v.bit.alpha(hidden ? 0 : baseA);
        v.bit.setTint(tint);
      }

      // press piston (only while it stamps: at rest it is the one drawn into the body)
      const work = running(d, b);
      const pp = b.type === 'press' ? partPose('press', 'piston', bx, by, wu) : null;
      const stamping = pp !== null && Math.abs(an.piston.dy) > 1e-3;
      v.piston = this.part(
        v.piston,
        stamping ? frameId.part('press', 'piston', 0) : null,
        DEPTH.part,
      );
      if (v.piston !== null && pp !== null) {
        v.piston.place(pp.x, pp.y + an.piston.dy, pp.w, pp.h);
        v.piston.alpha(hidden ? 0 : baseA);
      }
      // grinder wheel (turns while running; keeps its angle when it stops)
      const wp = b.type === 'grinder' ? partPose('grinder', 'wheel', bx, by, wu) : null;
      v.wheel = this.part(v.wheel, wp ? frameId.part('grinder', 'wheel', 0) : null, DEPTH.part);
      if (v.wheel !== null && wp !== null) {
        v.wheel.place(wp.x, wp.y, wp.w, wp.h);
        v.wheel.angle(wheelAngle(t, m.grinderDegPerSec, work, mode));
        v.wheel.alpha(hidden ? 0 : baseA);
        v.wheel.setTint(tint);
      }
      // smelter mouth glow (additive, pulses while running)
      const gp = b.type === 'smelter' ? partPose('smelter', 'glow', bx, by, wu) : null;
      const ga = gp
        ? glowAlpha(t, m.smelterGlowHz, m.smelterGlowMin, m.smelterGlowMax, work, mode)
        : 0;
      v.glow = this.part(
        v.glow,
        gp && ga > 0 ? frameId.part('smelter', 'glow', 0) : null,
        DEPTH.partGlow,
      );
      if (v.glow !== null && gp !== null) {
        v.glow.place(gp.x, gp.y, gp.w, gp.h);
        v.glow.blend(true);
        v.glow.alpha(hidden ? 0 : ga);
      }

      // facing arrow at the front edge
      const facing = FACING.has(b.type);
      v.arrow = this.part(v.arrow, facing ? frameId.arrow(b.dir) : null, DEPTH.arrow);
      if (v.arrow !== null) {
        const p = arrowPose(bx, by, wu, geo, b.dir, k.sx);
        v.arrow.place(p.x, p.y, p.w, p.h);
        v.arrow.alpha(hidden ? 0 : 1);
      }

      // chute chevron (one global clock); a corner's one-cell overlay follows its bend
      const bend =
        b.type === 'chute' && isBent(b.inDir, b.dir) ? cornerChevron(b.inDir, b.dir) : null;
      v.chev = this.part(
        v.chev,
        b.type !== 'chute'
          ? null
          : bend !== null
            ? frameId.chevronCorner(phase)
            : frameId.chevron(b.dir, phase),
        DEPTH.chevron,
      );
      if (v.chev !== null) {
        const size = bend !== null ? wu : wu * fx.chevronSize;
        v.chev.place(cx, cy, size, size);
        v.chev.angle(bend !== null ? bend.deg : 0);
        v.chev.flip(bend?.flip === true);
        v.chev.alpha(hidden ? 0 : 1);
      }

      // progress bar: the baked bar cropped to the progress (no Graphics); above a south arrow
      const prog = b.ghost === null ? progressPermille(d, b) : null;
      v.barBg = this.part(v.barBg, prog !== null ? frameId.barBg : null, DEPTH.barBg);
      v.barFill = this.part(v.barFill, prog !== null ? frameId.barFill : null, DEPTH.barFill);
      if (v.barBg !== null && v.barFill !== null && prog !== null) {
        // centre origin: a crop keeps the frame's left part where it is, so the fill grows rightwards
        const p = barPose(bx, by, wu, geo, facing && b.dir === 'S');
        v.barBg.place(p.x, p.y, p.w, p.h);
        v.barFill.place(p.x, p.y, p.w, p.h);
        const f = this.frames.get(frameId.barFill);
        v.barFill.cropLeft(Math.max(1, Math.floor((f.w * prog) / 1000)), f.h);
        v.barBg.alpha(hidden ? 0 : 1);
        v.barFill.alpha(hidden ? 0 : 1);
      }

      // the Gallery's mounted gun (unbadged, turned up at the rail) and its rail targets
      const gallery = b.type === 'gallery';
      v.gun = this.part(
        v.gun,
        gallery && b.gun !== null ? frameId.gunMount(b.gun.tier) : null,
        DEPTH.gun,
      );
      if (v.gun !== null) {
        const g = gunPose(bx, by, wu, geo);
        v.gun.place(g.x + an.gun.dx, g.y + an.gun.dy, g.w * k.sx, g.h * k.sy);
        v.gun.angle(g.rot + an.gun.rot);
        v.gun.alpha(hidden ? 0 : baseA * clamp01(an.gun.a));
      }
      this.syncTargets(v, b, an, bx, by, hidden ? 0 : baseA);

      // stall icon (top-right, 36% of the cell, ≥ stallBadgeMinPx CSS px); a stall without a badge frame
      // (stalls.json glyph null, e.g. NO_INPUT) shows none
      const shown = shownStall(d, b);
      const st = shown !== null && this.frames.has(frameId.stallBadge(shown)) ? shown : null;
      v.stall = this.part(v.stall, st !== null ? frameId.stallBadge(st) : null, DEPTH.stall);
      v.stallId = st;
      if (v.stall !== null) {
        stalls += 1;
        const p = stallPose(cx, cy, wu, share);
        const err = d.stalls.find((x) => x.id === st)?.cls === 'error';
        v.stall.place(p.x, p.y, p.w * an.stall.sx, p.h * an.stall.sy);
        v.stall.alpha(hidden ? 0 : err ? pulse : 1);
      }
      // the Gallery's "No ammo" badge (UX §5 GalleryDry; §6.5 Waiting: static amber square, empty tin)
      const dry =
        st === null &&
        b.type === 'gallery' &&
        galleryDry(b, d, this.c.galleryDryMs) &&
        this.frames.has(frameId.galleryDry);
      v.dry = this.part(v.dry, dry ? frameId.galleryDry : null, DEPTH.stall);
      if (v.dry !== null) {
        drys += 1;
        const p = stallPose(cx, cy, wu, share);
        v.dry.place(p.x, p.y, p.w, p.h);
        v.dry.alpha(hidden ? 0 : 1);
      }

      // contents indicators (what is inside)
      this.overlays.length = 0;
      contentsOverlays(b, d, bx, by, wu, geo, this.overlays);
      this.syncExtras(v, hidden ? 0 : baseA);
    }
    this.stalls = stalls;
    this.drys = drys;
    const stale: number[] = [];
    for (const [id, v] of this.views) if (v.gen !== this.gen) stale.push(id);
    for (const id of stale) this.drop(id);
    // anim records made for a building that never got a view (placed and removed between two frames)
    if (this.anim.size > this.views.size)
      for (const id of [...this.anim.keys()]) if (!this.views.has(id)) this.anim.delete(id);
  }

  /** The three standing rail targets: the kind of the last fired ammo tier (else the queue head's). */
  private syncTargets(
    v: BView,
    b: Building,
    an: BldAnims,
    bx: number,
    by: number,
    a: number,
  ): void {
    const want = b.type === 'gallery' ? TARGET_SLOTS : 0;
    while (v.targets.length > want) {
      const sp = v.targets.pop();
      if (sp) this.pool.release(sp);
    }
    if (b.type !== 'gallery') return;
    const tier = an.targetTier > 0 ? an.targetTier : (b.queue[0]?.tier ?? 1);
    const kinds = this.c.byAmmoTier;
    const kind = kinds[Math.max(0, Math.min(kinds.length - 1, tier - 1))] ?? 'bottle';
    const frame = frameId.target(kind);
    for (let i = 0; i < want; i++) {
      let sp = v.targets[i];
      if (sp === undefined) {
        sp = this.pool.acquire(frame, DEPTH.railTarget);
        v.targets.push(sp);
      } else this.pool.frame(sp, frame);
      const ta = an.targets[i];
      const p = targetPose(bx, by, this.wu, this.c.geo, i);
      sp.place(p.x + (ta?.dx ?? 0), p.y + (ta?.dy ?? 0), p.w, p.h);
      sp.angle(ta?.rot ?? 0);
      sp.alpha(a * clamp01(ta?.a ?? 1));
    }
  }

  /** Matches the view's extra sprites to `this.overlays` (acquire / reframe / release). */
  private syncExtras(v: BView, a: number): void {
    const want = this.overlays;
    while (v.extras.length > want.length) {
      const sp = v.extras.pop();
      if (sp) this.pool.release(sp);
    }
    for (let i = 0; i < want.length; i++) {
      const o = want[i] as Overlay;
      const depth =
        o.frame.startsWith('mark.digit.') || o.crop !== undefined
          ? DEPTH.contentsTop
          : DEPTH.contents;
      let sp = v.extras[i];
      if (sp === undefined) {
        sp = this.pool.acquire(o.frame, depth);
        v.extras.push(sp);
      } else {
        this.pool.frame(sp, o.frame);
        sp.setDepth(depth);
      }
      sp.place(o.x, o.y, o.w, o.h);
      sp.angle(o.rot);
      sp.alpha(a);
      if (o.crop !== undefined) {
        const f = this.frames.get(o.frame);
        sp.cropLeft(Math.max(1, Math.floor((f.w * o.crop) / 1000)), f.h);
      } else sp.cropLeft(-1, 0);
    }
  }

  private drop(id: number): void {
    const v = this.views.get(id);
    if (v === undefined) return;
    for (const p of [
      v.body,
      v.head,
      v.bit,
      v.piston,
      v.wheel,
      v.glow,
      v.arrow,
      v.barBg,
      v.barFill,
      v.gun,
      v.chev,
      v.stall,
      v.dry,
      ...v.targets,
      ...v.extras,
    ])
      if (p !== null) this.pool.release(p);
    this.views.delete(id);
    this.anim.delete(id);
  }

  /** CSS px per cell (relayout): sizes the stall badge's CSS-px minimum. */
  setCellCss(px: number): void {
    this.cellCss = px;
  }

  /** M6: hide building `id` (null = none) while it is dragged. */
  setHidden(id: number | null): void {
    this.hidden = id ?? -1;
  }

  /** Every anim back to rest (a rebuild: the FX were cleared). */
  restAll(): void {
    for (const a of this.anim.values()) resetBldAnims(a);
  }

  worldPos(id: number): { x: number; y: number } | null {
    const v = this.views.get(id);
    return v === undefined ? null : { x: v.wx, y: v.wy };
  }

  clear(): void {
    for (const id of [...this.views.keys()]) this.drop(id);
    this.anim.clear();
  }

  get count(): number {
    return this.views.size;
  }

  get stallCount(): number {
    return this.stalls;
  }

  /** Galleries showing the "No ammo" badge. */
  get dryCount(): number {
    return this.drys;
  }
}

function clamp01(a: number): number {
  return a < 0 ? 0 : a > 1 ? 1 : a;
}
